import "jsoneditor-react/es/editor.min.css";

/* eslint-disable react/display-name */
import { yupResolver } from "@hookform/resolvers/yup";
import Ajv from "ajv";
import React, { FormEvent, useEffect, useRef, useState } from "react";
import {
  Button,
  Col,
  Dropdown,
  Form,
  InputGroup,
  Modal,
  Row,
  Spinner
} from "react-bootstrap";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  BiArrowBack,
  BiChevronDown,
  BiSolidMegaphone,
  BiSolidMinusCircle,
  BiSolidPlusCircle
} from "react-icons/bi";
import { toast } from "react-toastify";
import * as Yup from "yup";

import i18n from "../../../../i18n/config";
import { BlockType } from "../../../../interfaces/models/Knowledge/scenario.interface";
import HintBlock from "../../../common/HintBlock";
import PopoverWithAudio from "../../../common/PopoverWithAudio";
import NodeParentChanger from "../NodeParentChanger";
import ScenarioSide from "./scenario-side";
import ScenarioJSONInput from "./scenario-json-input";
import DeleteButton from "../DeleteButton";
import ScenarioHeader from "../ScenarioHeader";
import { MacBotActionKind } from "../types";
import { ScenarioSimpleTextInput } from "./scenario-simple-text-input";
import AddNoteButton from "../AddNoteButton";
import NoteForm, { getNoteSchema, INoteForm } from "../NoteForm";
import { v4 as uuidv4 } from "uuid";
import { selectUseId } from "../../../../features/auth/auth.slice";
import { useAppSelector } from "../../../../app/hook";
import CopyVariable from "../../../common/CopyVariable";

const popupModalCatchResponseAudioPath: { [key: string]: string } = {
  en: "https://cdn.1cdn.app/application/LOUISEBOT/24101622190239_catch_reponse_en.mp3",
  fr: "https://cdn.1cdn.app/application/LOUISEBOT/24101622190271_catch_reponse_fr.mp3"
};

const popupModalAddBodyAudioPath: { [key: string]: string } = {
  en: "https://cdn.1cdn.app/application/LOUISEBOT/24101622380637_json_body_en.mp3",
  fr: "https://cdn.1cdn.app/application/LOUISEBOT/24101622380656_json_body_fr.mp3"
};

const popupModalMapResponseAudioPath: { [key: string]: string } = {
  en: "https://cdn.1cdn.app/application/LOUISEBOT/24101622580441_associate_values_en.mp3",
  fr: "https://cdn.1cdn.app/application/LOUISEBOT/2410162258045_associate_values_fr.mp3"
};

const CustomToggle = React.forwardRef(
  (
    {
      children,
      onClick
    }: { children: React.ReactNode; onClick: (e: any) => void },
    ref: any
  ) => (
    <button
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
      className="tw-h-11 tw-w-full tw-px-3 tw-py-2.5 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-solid tw-border-gray-300 tw-justify-between tw-items-center tw-inline-flex"
    >
      {children}
      <BiChevronDown size={16} className="tw-text-gray-400 ms-2" />
    </button>
  )
);

export interface IFormData extends INoteForm {
  title: string;
  method: string;
  url: string;
  headers?: Header[];
  parameters?: Header[];
  // body?: Body[];
  body?: string;
  responseParser?: IResponseParser[];
}

export interface IResponseParser {
  oldValue?: string | null;
  newValue: string;
  key: string;
}

export interface Header {
  key: string;
  value: string;
}

export interface Body {
  key: string;
  value: string;
}

const ajv = new Ajv({ allErrors: true, verbose: true, strict: false });

const editorSchema = {
  type: "object"
};

export const MacBotHTTPRequestForm = ({
  setShow,
  itemData,
  parentId,
  tree,
  loading,
  createScenarioBlock,
  deleteScenarioBlock,
  changeScenarioBlock,
  config
}: {
  setShow: (item: boolean) => void;
  itemData: any;
  loading: boolean;
  createScenarioBlock: (block: any) => Promise<any>;
  deleteScenarioBlock: (block: any) => void;
  changeScenarioBlock: (block: any) => Promise<any>;
  parentId: string | null;
  config: any;
  tree: any[];
}) => {
  const { t } = useTranslation();
  const userId = useAppSelector(selectUseId);
  const jsonEditorRef = useRef<HTMLDivElement | null>(null);

  const [isDelete, setIsDelete] = useState(true);
  const [showTesterModal, setShowTesterModal] = useState(false);
  const [rowNumbers, setRowNumbers] = useState<number[]>([]);
  const [showMapGuide, setShowMapGuide] = useState(false);
  const [testApiResponse, setTestApiResponse] = useState<{
    data?: any;
    status: number;
    error?: any;
  }>({
    data: null,
    status: 0
  });
  const [testLoading, setTestLoading] = useState(false);

  const schema = Yup.object({
    title: Yup.string()
      .trim()
      .required(t("McBotHttpForm/scenario_http_request_title_required")),
    method: Yup.string()
      .oneOf(["POST", "PUT", "PATCH", "GET", "DELETE"])
      .required(t("McBotHttpForm/scenario_http_request_method_required")),
    url: Yup.string()
      .url()
      .matches(
        /^https:\/\//,
        t("McBotHttpForm/scenario_http_request_url_required")
      )
      .required(t("McBotHttpForm/scenario_http_request_url_required")),
    headers: Yup.array()
      .of(
        Yup.object().shape({
          key: Yup.string()
            .trim()
            .required(
              t("McBotHttpForm/scenario_http_request_header_key_required")
            ),
          value: Yup.string()
            .trim()
            .required(
              t("McBotHttpForm/scenario_http_request_header_value_required")
            )
        })
      )
      .optional()
      .default([]),
    parameters: Yup.array()
      .of(
        Yup.object().shape({
          key: Yup.string()
            .trim()
            .required(
              t("McBotHttpForm/scenario_http_request_parameter_key_required")
            ),
          value: Yup.string()
            .trim()
            .required(
              t("McBotHttpForm/scenario_http_request_parameter_value_required")
            )
        })
      )
      .optional()
      .default([]),
    body: Yup.string().optional().default(""),
    responseParser: Yup.array()
      .of(
        Yup.object().shape({
          key: Yup.string().trim().optional().nullable(),
          oldValue: Yup.string().trim().optional().nullable(),
          newValue: Yup.string()
            .trim()
            .required(
              t("McBotHttpForm/scenario_http_request_map_value_required")
            )
        })
      )
      .optional()
      .default([]),
    ...getNoteSchema(t)
  });

  const cleanText = (text: string) => {
    if (text.trim() == "") {
      return text;
    }

    const fixedJsonStr = text.replace(/(\w+):/g, '"$1":').replace(/\\"/g, '"');

    try {
      // Parse the fixed string to ensure it's valid JSON
      const jsonObj = JSON.parse(fixedJsonStr);

      // Convert back to a properly formatted JSON string
      text = JSON.stringify(jsonObj, null, 2);
    } catch (error) {
      text = fixedJsonStr;
    }

    return text
      .replace(/[\n\r\t]/g, "") // Remove \n, \r, \t
      .replace(/\s+/g, " ") // Replace multiple spaces with a single space
      .trim(); // Remove leading and trailing spaces
  };

  const {
    register,
    formState: { errors },
    reset,
    watch,
    getValues,
    setValue,
    handleSubmit,
    setError,
    clearErrors,
    control,
    trigger
  } = useForm<IFormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      title: itemData?.title ?? "",
      method: itemData?.method ?? "GET",
      url: itemData?.url ?? "",
      headers: itemData?.headers ?? [],
      parameters: itemData?.parameters ?? [],
      body: itemData?.body ?? "",
      responseParser: [],
      notes: [
        {
          note_id: uuidv4(),
          creation_date_time: new Date().toISOString(),
          title: "",
          description: "",
          qc: false,
          author: userId
        }
      ]
    }
  });

  const {
    fields: headerFields,
    append: headerAppend,
    remove: headerRemove
  } = useFieldArray({
    control,
    name: "headers"
  });

  const {
    fields: parameterFields,
    append: parameterAppend,
    remove: parameterRemove
  } = useFieldArray({
    control,
    name: "parameters"
  });

  const {
    fields: responseParserFields,
    append: responseParserAppend,
    remove: responseParserRemove
  } = useFieldArray({
    control,
    name: "responseParser"
  });

  const { fields: noteFields } = useFieldArray({
    control,
    name: "notes"
  });

  const [contextMenuList, setContextMenuList] = useState<any[]>([]);

  const methodLabel: Record<string, React.ReactNode> = {
    POST: (
      <div className="tw-h-6 tw-px-2 tw-py-0.5 tw-bg-emerald-500 tw-rounded tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex">
        <div className="tw-text-white tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
          Post
        </div>
      </div>
    ),
    PATCH: (
      <div className="tw-h-6 tw-px-2 tw-py-0.5 tw-bg-teal-500 tw-rounded tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex">
        <div className="tw-text-white tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
          Patch
        </div>
      </div>
    ),
    PUT: (
      <div className="tw-h-6 tw-px-2 tw-py-0.5 tw-bg-orange-500 tw-rounded tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex">
        <div className="tw-text-white tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
          Put
        </div>
      </div>
    ),
    DELETE: (
      <div className="tw-h-6 tw-px-2 tw-py-0.5 tw-bg-red-500 tw-rounded tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex">
        <div className="tw-text-white tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
          Delete
        </div>
      </div>
    ),
    GET: (
      <div className="tw-h-6 tw-px-2 tw-py-0.5 tw-bg-sky-500 tw-rounded tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex">
        <div className="tw-text-white tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
          Get
        </div>
      </div>
    )
  };

  const validate = (json: any) => {
    clearErrors("body");

    const valid = ajv.validate(editorSchema, json);
    if (!valid) {
      setError("body", {
        type: "custom",
        message: t("McBotHttpForm/key1")
      });
    }
    return valid;
  };

  const handleJsonChange = (json: any) => {
    setValue("body", json ?? "");
  };

  const deleteHandler = () => {
    deleteScenarioBlock(itemData);
    setShow(false);
  };

  const onSubmit = (data: any) => {
    if (itemData) {
      const _data = {
        ...itemData,
        id: itemData.id,
        type: BlockType.HTTP,
        parent: itemData.parent,
        ...data,
        body: cleanText(data.body),
        responseParser:
          data.responseParser?.map((dr: IResponseParser) => {
            return {
              newValue: dr.newValue,
              oldValue: dr.oldValue
            };
          }) ?? []
      };
      changeScenarioBlock(_data).then(() => {
        reset();
        setShow(false);
      });
    } else {
      const _data = {
        type: BlockType.HTTP,
        parent: parentId,
        ...data,
        body: cleanText(data.body),
        responseParser:
          data.responseParser?.map((dr: IResponseParser) => {
            return {
              newValue: dr.newValue,
              oldValue: dr.oldValue
            };
          }) ?? []
      };
      createScenarioBlock(_data).then(() => {
        reset();
        setShow(false);
      });
    }
  };

  const extractVariables = (url: string) => {
    // Split URL at the question mark
    const urlParts = url.split("/");

    // Check for variables in each segment
    const variables = [];
    for (const part of urlParts) {
      if (part.startsWith("$:var_") && part.endsWith("!")) {
        variables.push(part);
      }
    }

    return variables;
  };

  const handleTestUrl = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setTestLoading(true);

    const formData = new FormData(e.currentTarget);

    const url = formData.get("url") as string;
    const urlVariables = formData.getAll("urlParams[key]");
    const urlValues = formData.getAll("urlParams[value]");

    const method = formData.get("method") as string;

    const headerVariables = formData.getAll("headers[key]");
    const headerValues = formData.getAll("headers[value]");
    const nonVariableHeaders = getValues("headers")?.filter(
      (header) => !header.value.startsWith("$:var_")
    );

    const queryParamVariables = formData.getAll("parameters[key]");
    const queryParamValues = formData.getAll("parameters[value]");
    const nonVariableSearchParams = getValues("parameters")?.filter(
      (qp) => !qp.value.startsWith("$:var_")
    );

    const bodyVariables = formData.getAll("body[key]");
    const bodyValue = formData.getAll("body[value]");

    const object: Record<string, any> = {
      url,
      method,
      headers: [
        ...(nonVariableHeaders as Header[]),
        ...headerVariables.map((variable, index) => {
          return {
            key: cleanText(variable as string),
            value: cleanText(headerValues[index] as string)
          };
        })
      ],
      queryParam: [
        ...(nonVariableSearchParams as Header[]),
        ...queryParamVariables.map((variable, index) => {
          return {
            key: cleanText(variable as string),
            value: cleanText(queryParamValues[index] as string)
          };
        })
      ],
      body: bodyVariables.map((variable, index) => {
        return {
          key: cleanText(variable as string),
          value: cleanText(bodyValue[index] as string)
        };
      })
    };

    let newUrl = object.url.split("?")[0];

    for (const variable of urlVariables) {
      const index = urlVariables.indexOf(variable);
      newUrl = newUrl.replace(variable, urlValues[index]);
    }

    try {
      if (!url) throw new Error("URL is undefined or null");

      const params = new URL(url).searchParams;

      if (params) {
        const existingQueryParams = Array.from(params.entries()).map(
          ([key, value]) => ({ key, value })
        );

        // Ensure object.queryParam is an array
        if (!Array.isArray(object.queryParam)) {
          object.queryParam = [];
        }

        // Spread to avoid nested arrays
        object.queryParam = [...object.queryParam, ...existingQueryParams];
      }
    } catch (error) {
      console.error("Error processing URL parameters:", error);
    }

    if (object.queryParam && object.queryParam.length > 0) {
      const urlSearchParams = new URLSearchParams();

      object.queryParam.forEach((param: any) => {
        if (param.key && param.value)
          urlSearchParams.append(param.key, param.value);
      });
      newUrl = `${newUrl}?${decodeURIComponent(urlSearchParams.toString())}`;
    }

    function headersArrayToJSON(
      headersArray: Array<{ key: string; value: string }>
    ) {
      const headersJSON: Record<string, string> = {};

      headersArray.forEach((header) => {
        headersJSON[header.key] = header.value;
      });

      return headersJSON;
    }

    try {
      new URL(newUrl);
    } catch (error: any) {
      toast.error(t("McBotHttpForm/key26") + " " + error?.message);
      return;
    }

    fetch(newUrl, {
      method: object.method,
      headers: headersArrayToJSON(object.headers),
      body: object.method !== "GET" ? getValues("body") : undefined
    })
      .then(async (response) => {
        const contentType = response.headers.get("content-type");

        if (!contentType) {
          console.warn("No content-type header found.");
          setTestApiResponse({ status: response.status, data: {} });
          return;
        }

        const textResponse = await response.text(); // Read response as text

        let parsedData = {};

        if (textResponse) {
          try {
            parsedData = contentType.includes("application/json")
              ? JSON.parse(textResponse)
              : textResponse;
          } catch (error) {
            console.error("JSON Parsing Error:", error);
          }
        }

        setTestApiResponse({
          status: response.status,
          data: parsedData
        });

        setTestLoading(false);
      })
      .catch((err) => {
        console.error("Fetch Error:", err);
        toast.error(t("McBotHttpForm/key26") + " " + err?.message);

        setTestLoading(false);
      });
  };

  useEffect(() => {
    if (!itemData) {
      setIsDelete(false);
    } else {
      reset({
        title: itemData?.title ?? "",
        method: itemData?.method ?? "GET",
        url: itemData?.url ?? "",
        headers: itemData?.headers ?? [],
        parameters: itemData?.parameters ?? [],
        body: itemData?.body ?? "",
        responseParser:
          itemData.responseParser?.map((dr: IResponseParser) => {
            return {
              newValue: dr.newValue,
              oldValue: dr.newValue,
              key: dr.key
            };
          }) ?? [],
        notes: itemData?.notes
      });
    }
  }, [itemData]);

  useEffect(() => {
    if (
      itemData &&
      itemData.responseParser &&
      watch("responseParser") &&
      (itemData.responseParser as any[]).length >
        (watch("responseParser") as any[]).length
    ) {
      setValue(
        "responseParser",
        itemData.responseParser.map((dr: any) => {
          return {
            oldValue: dr.newValue,
            newValue: dr.newValue,
            key: dr.key
          };
        })
      );
    }
  }, [itemData]);

  useEffect(() => {
    return () => {
      setTestApiResponse({
        status: 0,
        data: null
      });
    };
  }, [showTesterModal]);

  useEffect(() => {
    if (config.metaData.variables) {
      setContextMenuList([...config.metaData.variables]);
    }
  }, [config]);

  return (
    <React.Fragment>
      <ScenarioSide size="lg" onSubmit={handleSubmit(onSubmit)}>
        <ScenarioHeader
          type={MacBotActionKind.ShowHTTPRequestForm}
          onClose={() => {
            reset();
            setShow?.(false);
          }}
        />

        <div className="md:tw-p-6 tw-p-4 tw-bg-gray-50 tw-flex tw-flex-col tw-gap-y-4 tw-w-full">
          <Row>
            <Form.Group className="tw-w-full">
              <Form.Label
                htmlFor=""
                className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none"
              >
                {t("McBotHttpForm/key4")}
              </Form.Label>
              <Form.Control
                className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                maxLength={30}
                max={30}
                type="text"
                isInvalid={!!errors.title}
                {...register("title")}
              />
              <HintBlock texts={["McBotHttpForm/maxlenght"]} />
              <Form.Control.Feedback
                type="invalid"
                className="d-block font-poppins tw-mt-3 tw-text-sm"
              >
                {errors.title?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          <div className="h-[0px] border border-gray-200"></div>

          <Row className="gap-2">
            <Col xs={12}>
              <Form.Label
                htmlFor=""
                className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none"
              >
                {t("McBotHttpForm/key32")}
              </Form.Label>
              <Dropdown>
                <Dropdown.Toggle as={CustomToggle} id="method-selector">
                  {methodLabel[watch("method")]}
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  <Dropdown.Item
                    eventKey="1"
                    onClick={() => setValue("method", "POST")}
                    className={
                      watch("method") === "POST"
                        ? " tw-px-4 tw-py-2.5 tw-bg-gray-100 tw-rounded tw-justify-start tw-items-center tw-gap-3 tw-inline-flex"
                        : ""
                    }
                  >
                    {methodLabel["POST"]}
                    {/* <span className="tw-ml-2">Post</span> */}
                  </Dropdown.Item>
                  <Dropdown.Item
                    eventKey="2"
                    onClick={() => setValue("method", "PUT")}
                    className={
                      watch("method") === "PUT"
                        ? " tw-px-4 tw-py-2.5 tw-bg-gray-100 tw-rounded tw-justify-start tw-items-center tw-gap-3 tw-inline-flex"
                        : ""
                    }
                  >
                    {methodLabel["PUT"]}
                    {/* <span className="tw-ml-2">Put</span> */}
                  </Dropdown.Item>
                  <Dropdown.Item
                    eventKey="3"
                    onClick={() => setValue("method", "PATCH")}
                    className={
                      watch("method") === "PATCH"
                        ? " tw-px-4 tw-py-2.5 tw-bg-gray-100 tw-rounded tw-justify-start tw-items-center tw-gap-3 tw-inline-flex"
                        : ""
                    }
                  >
                    {methodLabel["PATCH"]}
                    {/* <span className="tw-ml-2">Patch</span> */}
                  </Dropdown.Item>
                  <Dropdown.Item
                    eventKey="4"
                    onClick={() => setValue("method", "DELETE")}
                    className={
                      watch("method") === "DELETE"
                        ? " tw-px-4 tw-py-2.5 tw-bg-gray-100 tw-rounded tw-justify-start tw-items-center tw-gap-3 tw-inline-flex"
                        : ""
                    }
                  >
                    {methodLabel["DELETE"]}
                    {/* <span className="tw-ml-2">Delete</span> */}
                  </Dropdown.Item>
                  <Dropdown.Item
                    eventKey="5"
                    onClick={() => setValue("method", "GET")}
                    className={
                      watch("method") === "GET"
                        ? " tw-px-4 tw-py-2.5 tw-bg-gray-100 tw-rounded tw-justify-start tw-items-center tw-gap-3 tw-inline-flex"
                        : ""
                    }
                  >
                    {methodLabel["GET"]}
                    {/* <span className="tw-ml-2">Get</span> */}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Col>
            <Col xs={12}>
              <InputGroup className="tw-relative">
                <Form.Label
                  htmlFor=""
                  className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none"
                >
                  {t("McBotHttpForm/key31")}
                </Form.Label>
                <ScenarioSimpleTextInput
                  placeholder="https://example.com/user/$:var_my_user/"
                  setValue={(text) => {
                    setValue("url", text?.replaceAll(/(\r\n|\n|\r)/gm, ""));

                    trigger("url").then(() => {
                      clearErrors("url");
                    });
                  }}
                  value={itemData?.url ?? getValues("url")}
                  contextMenuList={contextMenuList}
                  isInvalid={!!errors.url}
                />
                <Form.Control.Feedback
                  type="invalid"
                  className="d-block font-poppins"
                >
                  {errors?.url?.message}
                </Form.Control.Feedback>
              </InputGroup>
            </Col>
          </Row>

          <div className="h-[0px] border border-gray-200"></div>

          <Row>
            <Col xs={12}>
              <div className="tw-flex tw-flex-col tw-gap-3">
                <div className="tw-flex tw-items-center tw-justify-between">
                  <div className="tw-text-gray-400 tw-text-base tw-font-semibold tw-font-['Lexend'] tw-leading-normal">
                    {t("McBotHttpForm/key5")}
                  </div>
                </div>

                {headerFields.map((field, index) => (
                  <Row
                    key={field.id}
                    className={`tw-mt-2 tw-items-start ${
                      index + 1 < headerFields.length ? "mb-3" : ""
                    } `}
                  >
                    <Col md={11} xs={10}>
                      <Row className="gap-3">
                        <Col xs={12}>
                          <Form.Group className="tw-relative">
                            <ScenarioSimpleTextInput
                              placeholder={t("McBotHttpForm/key6").toString()}
                              setValue={(text) => {
                                setValue(`headers.${index}.key`, text);
                              }}
                              value={getValues(`headers.${index}.key`)}
                              contextMenuList={contextMenuList}
                              isInvalid={!!errors.headers?.[index]?.key}
                            />
                            <Form.Control.Feedback
                              type="invalid"
                              className="tw-block"
                            >
                              {errors.headers?.[index]?.key?.message}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col xs={12}>
                          <Form.Group className="tw-relative">
                            <ScenarioSimpleTextInput
                              placeholder={t("McBotHttpForm/key7").toString()}
                              setValue={(text) => {
                                setValue(`headers.${index}.value`, text);
                              }}
                              value={getValues(`headers.${index}.value`)}
                              contextMenuList={contextMenuList}
                              isInvalid={!!errors.headers?.[index]?.value}
                            />
                            <Form.Control.Feedback
                              type="invalid"
                              className="tw-block"
                            >
                              {errors.headers?.[index]?.value?.message}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                    </Col>

                    <Col md={1} xs={2} className="tw-self-stretch">
                      <button
                        type="button"
                        className="tw-flex tw-flex-col tw-min-h-[116px] tw-h-full tw-flex-1 tw-p-2.5 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-solid tw-border-gray-300 tw-justify-center tw-items-center tw-gap-2"
                        onClick={() => headerRemove(index)}
                      >
                        <BiSolidMinusCircle
                          className="tw-text-red-400"
                          size={20}
                        />
                      </button>
                    </Col>
                  </Row>
                ))}

                <button
                  type="button"
                  onClick={() => headerAppend({ key: "", value: "" })}
                  className="tw-h-10 tw-w-full tw-pl-[15px] tw-pr-5 tw-py-2.5 tw-bg-white tw-rounded-[99px] tw-shadow tw-border tw-border-solid tw-border-gray-300 tw-justify-center tw-items-center tw-gap-2 tw-inline-flex"
                >
                  <BiSolidPlusCircle className="tw-text-gray-400" />
                  <span className="tw-text-gray-800 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                    Header
                  </span>
                </button>
              </div>
            </Col>
          </Row>

          <div className="h-[0px] border border-gray-200"></div>

          {/* Search parameters */}
          <Row>
            <Col xs={12}>
              <div className="tw-flex tw-flex-col tw-gap-3">
                <div className="tw-flex tw-items-center tw-justify-between">
                  <div className="tw-text-gray-400 tw-text-base tw-font-semibold tw-font-['Lexend'] tw-leading-normal">
                    {t("McBotHttpForm/key8")}
                  </div>
                </div>

                {parameterFields.map((field, index) => (
                  <Row
                    key={field.id}
                    className={`tw-mt-2 tw-items-start ${
                      index + 1 < parameterFields.length ? "mb-3" : ""
                    } `}
                  >
                    <Col md={11} xs={10}>
                      <Row className="gap-3">
                        <Col xs={12}>
                          <Form.Group className="tw-relative">
                            <ScenarioSimpleTextInput
                              placeholder={t("McBotHttpForm/key6").toString()}
                              setValue={(text) => {
                                setValue(`parameters.${index}.key`, text);
                              }}
                              value={getValues(`parameters.${index}.key`)}
                              contextMenuList={contextMenuList}
                              isInvalid={!!errors.parameters?.[index]?.key}
                            />
                            <Form.Control.Feedback
                              type="invalid"
                              className="tw-block"
                            >
                              {errors.parameters?.[index]?.key?.message}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col xs={12}>
                          <Form.Group className="tw-relative">
                            <ScenarioSimpleTextInput
                              placeholder={t("McBotHttpForm/key7").toString()}
                              setValue={(text) => {
                                setValue(`parameters.${index}.value`, text);
                              }}
                              value={getValues(`parameters.${index}.value`)}
                              contextMenuList={contextMenuList}
                              isInvalid={!!errors.parameters?.[index]?.value}
                            />
                            <Form.Control.Feedback
                              type="invalid"
                              className="tw-block"
                            >
                              {errors.parameters?.[index]?.value?.message}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                    </Col>

                    <Col md={1} xs={2} className="tw-self-stretch">
                      <button
                        type="button"
                        className="tw-flex tw-flex-col tw-min-h-[116px] tw-h-full tw-flex-1 tw-p-2.5 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-solid tw-border-gray-300 tw-justify-center tw-items-center tw-gap-2"
                        onClick={() => parameterRemove(index)}
                      >
                        <BiSolidMinusCircle
                          className="tw-text-red-400"
                          size={20}
                        />
                      </button>
                    </Col>
                  </Row>
                ))}

                <button
                  type="button"
                  onClick={() => parameterAppend({ key: "", value: "" })}
                  className="tw-h-10 tw-w-full tw-pl-[15px] tw-pr-5 tw-py-2.5 tw-bg-white tw-rounded-[99px] tw-shadow tw-border tw-border-solid tw-border-gray-300 tw-justify-center tw-items-center tw-gap-2 tw-inline-flex"
                >
                  <BiSolidPlusCircle className="tw-text-gray-400" />
                  <span className="tw-text-gray-800 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                    {t("McBotHttpForm/key8")}
                  </span>
                </button>
              </div>
            </Col>
          </Row>

          <div className="h-[0px] border border-gray-200"></div>

          {/* Json editor */}
          <Row>
            <Col xs={12}>
              <div className="tw-flex tw-flex-col">
                <div className="tw-flex tw-justify-between tw-items-center">
                  <span className="tw-text-gray-400 tw-text-base tw-font-semibold tw-font-['Lexend'] tw-leading-normal">
                    JSON body
                    <PopoverWithAudio
                      data={{
                        contentText: t("McBotHttpForm/key19"),
                        audioUrl: popupModalAddBodyAudioPath[i18n.language],
                        size: 15
                      }}
                    />
                  </span>
                </div>

                <div id="json-body">
                  <Controller
                    control={control}
                    name="body"
                    render={({ field: { value, onChange } }) => (
                      <div>
                        <ScenarioJSONInput
                          setValue={(text) => {
                            handleJsonChange(text);
                          }}
                          placeholder=""
                          value={getValues("body") ?? ""}
                          contextMenuList={contextMenuList}
                          isInvalid={!!errors.body}
                        />
                        {rowNumbers.map((number, index) => (
                          <div key={index}>{number}</div>
                        ))}
                      </div>
                    )}
                  />
                  {!!errors.body && (
                    <Form.Control.Feedback
                      className="tw-block py-2"
                      type="invalid"
                    >
                      {errors.body?.message}
                    </Form.Control.Feedback>
                  )}
                </div>

                <button
                  type="button"
                  disabled={!watch("url")}
                  onClick={() => setShowTesterModal(true)}
                  className="tw-h-10 tw-w-full tw-pl-[15px] tw-pr-5 tw-py-2.5 tw-bg-white tw-rounded-[99px] tw-shadow tw-border tw-border-solid tw-border-gray-300 tw-justify-center tw-items-center tw-gap-2 tw-inline-flex tw-mt-3"
                >
                  {t("McBotHttpForm/key10")}
                </button>
              </div>
            </Col>
          </Row>

          <div className="h-[0px] border border-gray-200"></div>

          {/* Response mapper */}
          <Row>
            <Col xs={12}>
              <div className="tw-flex tw-flex-col tw-gap-3">
                <div className="tw-flex tw-items-center tw-justify-between">
                  <div className="tw-text-gray-400 tw-text-base tw-font-semibold tw-font-['Lexend'] tw-leading-normal">
                    {t("McBotHttpForm/key14")}
                    <PopoverWithAudio
                      data={{
                        contentText: t("McBotHttpForm/key21"),
                        audioUrl: popupModalMapResponseAudioPath[i18n.language],
                        size: 15
                      }}
                    />
                  </div>
                </div>
                <div className="tw-flex tw-flex-col tw-gap-3">
                  <button
                    type="button"
                    className="tw-text-blue-800 tw-bg-transparent tw-text-sm tw-font-normal tw-font-['Lexend'] tw-underline tw-leading-tight tw-border-none"
                    onClick={() => setShowMapGuide(true)}
                  >
                    {t("McBotHttpForm/key24")}
                  </button>
                </div>

                {responseParserFields.map((field, index) => (
                  <Row
                    key={field.id}
                    className={`tw-mt-2 tw-items-start ${
                      index + 1 < headerFields.length ? "mb-3" : ""
                    } `}
                  >
                    <Col md={11} xs={10}>
                      <Row className="gap-3">
                        <Col xs={12}>
                          <Form.Group>
                            <Controller
                              name={`responseParser.${index}.key`}
                              control={control}
                              render={() => (
                                <CopyVariable
                                  title={watch("title")}
                                  text={watch(
                                    `responseParser.${index}.newValue`
                                  )}
                                  noTitle
                                />
                              )}
                            />

                            <Form.Control.Feedback
                              type="invalid"
                              className="tw-block"
                            >
                              {errors.responseParser?.[index]?.key?.message}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>

                        <Col xs={12}>
                          <Form.Group>
                            <Form.Control
                              className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                              type="text"
                              placeholder={t("McBotHttpForm/key7").toString()}
                              isInvalid={
                                !!errors.responseParser?.[index]?.newValue
                              }
                              {...register(`responseParser.${index}.newValue`)}
                            />
                            <Form.Control.Feedback
                              type="invalid"
                              className="tw-block"
                            >
                              {
                                errors.responseParser?.[index]?.newValue
                                  ?.message
                              }
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                    </Col>

                    <Col md={1} xs={2} className="tw-self-stretch">
                      <button
                        type="button"
                        className="tw-flex tw-flex-col tw-min-h-[116px] tw-h-full tw-flex-1 tw-p-2.5 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-solid tw-border-gray-300 tw-justify-center tw-items-center tw-gap-2"
                        onClick={() => responseParserRemove(index)}
                      >
                        <BiSolidMinusCircle
                          className="tw-text-red-400"
                          size={20}
                        />
                      </button>
                    </Col>
                  </Row>
                ))}

                <button
                  type="button"
                  onClick={() => {
                    responseParserAppend({
                      newValue: "",
                      key: "",
                      oldValue: null
                    });
                  }}
                  className="tw-h-10 tw-w-full tw-pl-[15px] tw-pr-5 tw-py-2.5 tw-bg-white tw-rounded-[99px] tw-shadow tw-border tw-border-solid tw-border-gray-300 tw-justify-center tw-items-center tw-gap-2 tw-inline-flex"
                >
                  <BiSolidPlusCircle className="tw-text-gray-400" />

                  <span className="tw-text-gray-800 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                    {t("McBotHttpForm/key25")}
                  </span>
                </button>
              </div>
            </Col>
          </Row>
        </div>
        <div className="tw-flex flex-row tw-justify-between tw-items-center">
          <div className="tw-flex tw-justify-start tw-items-center tw-gap-2">
            {isDelete && <DeleteButton deleteHandler={deleteHandler} />}

            {itemData?.id && (
              <NodeParentChanger tree={tree} currentId={itemData.id} />
            )}
            <AddNoteButton noteFormMethods={{ getValues, setValue }} />
          </div>
          <div className="tw-flex tw-justify-end">
            <Button
              type="submit"
              className="tw-h-11 tw-px-5 tw-py-3 tw-bg-primary tw-rounded-[99px] tw-justify-center tw-items-center tw-inline-flex tw-z-20"
              disabled={loading}
            >
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                t("MacBotResponseForm/btn-02")
              )}
            </Button>
          </div>
        </div>
        <NoteForm
          noteFormMethods={{ register, watch, errors, fields: noteFields }}
        />
      </ScenarioSide>

      <Modal
        show={showMapGuide}
        onHide={() => setShowMapGuide(false)}
        size="lg"
        centered
      >
        <Modal.Body>
          <Row>
            <Col xs={12}>
              <div className="tw-flex tw-justify-between tw-items-center tw-pb-4">
                <span className="tw-text-gray-400 tw-text-base tw-font-semibold tw-font-['Lexend'] tw-leading-normal">
                  {t("McBotHttpForm/key11")}
                  <PopoverWithAudio
                    data={{
                      contentText: t("McBotHttpForm/key20"),
                      audioUrl: popupModalCatchResponseAudioPath[i18n.language],
                      size: 20
                    }}
                  />
                </span>
              </div>

              <div className="tw-min-h-[217px] tw-justify-start tw-items-start tw-gap-2 tw-grid tw-grid-cols-12 tw-w-full">
                <div className="md:tw-col-span-4 tw-col-span-12 tw-flex tw-flex-col tw-h-full">
                  <div className="tw-overflow-auto tw-h-full tw-pl-[17px] tw-pr-[13px] tw-py-2 tw-bg-gray-200 tw-rounded-md tw-shadow tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
                    <div className="tw-self-stretch justify-between tw-items-center tw-inline-flex">
                      <div className="tw-w-[219px] tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                        {t("McBotHttpForm/key12")}
                      </div>
                      <div className="tw-w-5 tw-h-5 tw-px-[5px] tw-py-[7px] tw-opacity-0 tw-flex-col tw-justify-center tw-items-center tw-inline-flex" />
                    </div>
                    <div className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                      <pre>
                        {JSON.stringify(
                          {
                            users: [{ name: "john", email: "john@mail.com" }]
                          },
                          null,
                          2
                        )}
                      </pre>
                    </div>
                  </div>
                </div>
                <div className="md:tw-col-span-8 tw-col-span-12 tw-grow tw-shrink tw-basis-0 tw-h-[217px] tw-justify-start items-center tw-gap-4 tw-flex">
                  <div className="tw-grow tw-shrink tw-basis-0 tw-h-[217px] tw-justify-start tw-items-start tw-gap-4 tw-flex">
                    <div className="tw-grow tw-shrink tw-basis-0 tw-h-[217px] tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-inline-flex">
                      <div className="tw-self-stretch tw-h-[217px] tw-pl-[17px] tw-pr-[13px] tw-py-2 tw-bg-white tw-rounded-md tw-shadow border tw-border-gray-300 tw-flex-col tw-justify-start tw-items-start tw-gap-4 tw-flex">
                        <div className="tw-self-stretch tw-justify-between tw-items-center tw-inline-flex">
                          <div className="tw-text-gray-500 tw-text-sm font-normal tw-font-['Lexend'] tw-leading-tight">
                            {t("McBotHttpForm/key13")}
                            <br />
                            users.0.email
                          </div>
                          <div className="tw-w-5 tw-h-5 tw-px-[5px] tw-py-[7px] tw-opacity-0 tw-flex-col tw-justify-center tw-items-center tw-inline-flex" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>

      <Modal
        show={showTesterModal}
        onHide={() => setShowTesterModal(false)}
        centered
      >
        <Modal.Header className="tw-p-3 tw-self-stretch tw-bg-gray-50 tw-rounded-tl-2xl tw-rounded-tr-2xl tw-justify-between tw-items-center tw-flex tw-w-full tw-border-0">
          <div className="tw-justify-start tw-items-center tw-gap-2 tw-flex">
            <button
              onClick={() => setShowTesterModal(false)}
              className="tw-h-11 tw-p-2.5 tw-bg-gray-100 tw-rounded-[99px] tw-border-none tw-justify-start tw-items-center tw-gap-3 tw-flex tw-flex-col"
            >
              <div className="tw-w-6 tw-h-6 relative">
                <BiArrowBack size={22} className="tw-text-gray-800" />
              </div>
            </button>
            <div className="tw-h-11 tw-p-2.5 tw-bg-gray-100 tw-rounded-[99px] tw-justify-start tw-items-center tw-gap-3 tw-inline-flex">
              <BiSolidMegaphone className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800" />
              <div className="tw-text-gray-800 tw-text-base tw-font-semibold tw-font-['Lexend'] tw-leading-normal">
                {t("McBotHttpForm/key17")}
              </div>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="md:tw-p-6 tw-p-4 tw-bg-gray-50 tw-flex tw-flex-col tw-gap-y-4 tw-w-full">
          <Form onSubmit={handleTestUrl}>
            <Form.Control hidden name="url" defaultValue={getValues("url")} />

            <Form.Control
              hidden
              name="method"
              defaultValue={getValues("method")}
            />

            {/* URL parameters */}
            {extractVariables(getValues("url")).length > 0 && (
              <div className="tw-text-gray-800 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
                {t("McBotHttpForm/key23")}
              </div>
            )}
            {extractVariables(getValues("url")).map((param, index) => (
              <Row key={index} className="g-3 mb-4">
                <Col md={12} xs={12}>
                  <Form.Group>
                    <Form.Control
                      defaultValue={param}
                      name={`urlParams[key]`}
                      readOnly
                      className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                    />
                  </Form.Group>
                </Col>
                <Col md={12} xs={12}>
                  <Form.Group>
                    <Form.Control
                      type="text"
                      name={`urlParams[value]`}
                      required
                      className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                    />
                  </Form.Group>
                </Col>
              </Row>
            ))}
            {extractVariables(getValues("url")).length > 0 && <hr />}

            {/* Headers */}
            {(getValues("headers")?.length as number) > 0 && (
              <div className="tw-text-gray-800 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7 tw-mb-3">
                {t("McBotHttpForm/key5")}
              </div>
            )}
            {getValues("headers")?.map((header, index) => {
              if (header.value?.startsWith("$:var_"))
                return (
                  <Row key={index} className="g-3 mb-4">
                    <Col md={12} xs={12}>
                      <Form.Group>
                        <Form.Control
                          defaultValue={header.key}
                          name={`headers[key]`}
                          readOnly
                          className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={12} xs={12}>
                      <Form.Group>
                        <Form.Control
                          type="text"
                          name={`headers[value]`}
                          required
                          className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                );
              else return null;
            })}

            {/* Search parameters */}
            {(getValues("parameters")?.length as number) > 0 && (
              <React.Fragment>
                <hr />
                <div className="tw-text-gray-800 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7 tw-mb-3">
                  {t("McBotHttpForm/key8")}
                </div>
              </React.Fragment>
            )}
            {getValues("parameters")?.map((queryParam, index) => {
              if (queryParam.value?.startsWith("$:var_"))
                return (
                  <Row key={index} className="g-3 mb-4">
                    <Col md={12} xs={12}>
                      <Form.Group>
                        <Form.Control
                          defaultValue={queryParam.key}
                          name={`parameters[key]`}
                          readOnly
                          className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={12} xs={12}>
                      <Form.Group>
                        <Form.Control
                          type="text"
                          name={`parameters[value]`}
                          required
                          className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                );
              else return null;
            })}

            <hr />

            {testApiResponse &&
              testApiResponse.data &&
              testApiResponse.status && (
                <div>
                  <div className="tw-h-7 tw-justify-start tw-items-center tw-gap-4 tw-inline-flex tw-mb-4">
                    <div className="tw-text-gray-800 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
                      HTTP Status code
                    </div>

                    {testApiResponse.status && (
                      <React.Fragment>
                        {testApiResponse.status < 400 ? (
                          <div className="tw-px-2 tw-bg-emerald-500 tw-rounded tw-justify-center tw-items-center tw-gap-2.5 tw-flex">
                            <div className="tw-text-white tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
                              200
                            </div>
                          </div>
                        ) : testApiResponse.status >= 400 &&
                          testApiResponse.status < 500 ? (
                          <div className="tw-px-2 tw-bg-orange-300 tw-rounded tw-justify-center tw-items-center tw-gap-2.5 tw-flex">
                            <div className="tw-text-white tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
                              400
                            </div>
                          </div>
                        ) : testApiResponse.status >= 500 ? (
                          <div className="tw-px-2 tw-bg-red-400 tw-rounded tw-justify-center tw-items-center tw-gap-2.5 tw-flex">
                            <div className="tw-text-white tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
                              500
                            </div>
                          </div>
                        ) : null}
                      </React.Fragment>
                    )}
                  </div>

                  <div className="tw-mb-4">
                    <div className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none tw-mb-3">
                      Response
                    </div>

                    <pre className="tw-border tw-border-solid tw-border-gray-300 tw-max-h-48 tw-rounded-md tw-p-2 tw-overflow-auto">
                      {JSON.stringify(testApiResponse.data, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            <div className="text-end">
              <Button
                disabled={testLoading}
                type="submit"
                className="tw-px-[17px] tw-py-2.5 disabled:tw-bg-gray-300 tw-rounded-[99px] tw-shadow tw-justify-center tw-items-center tw-flex tw-ml-auto"
              >
                {testLoading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <div className="tw-text-white tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                    {t("McBotHttpForm/key18")}
                  </div>
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};
